import { useDispatch, useSelector } from "react-redux";
import { Drawer } from "antd";
import { useForm } from "antd/lib/form/Form";
import { useEffect, useState } from "react";
import GenericFormComponent from "../../shared/components/generic-form/generic-form.component";
import { AppDispatch, RootState } from "../../shared/types/common";
import { showNotification } from "../../shared/utils/utils";
import {
  createDocumentSubType,
  updateDocumentSubType,
} from "../../core/store/reducers/document-sub-type.slice";
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";

interface Props {
  onClose: () => void;
  visible: boolean;
  selectedEntity?: any;
  onSubmit?: () => void;
  /** @deprecated Sub-types are no longer tied to a document type. */
  documentTypeList?: any[];
}

const CreateEditDocumentSubTypeComponent = ({
  onClose,
  visible,
  selectedEntity = null,
  onSubmit = () => {
    return;
  },
}: Props) => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const [form] = useForm();
  const { screenType } = useSelector((state: RootState) => state.settings);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (selectedEntity) {
      form.setFieldsValue({
        documentSubTypeName: selectedEntity?.documentSubTypeName,
        status: selectedEntity?.status,
      });
    } else {
      form.resetFields();
    }
  }, [form, selectedEntity]);

  const onFinish = async (values: any) => {
    setSaving(true);
    // No documentTypeId: sub-types are a standalone list, usable under any type.
    const payload = {
      documentSubTypeName: values?.documentSubTypeName,
      status: values?.status,
    };

    const action = selectedEntity
      ? updateDocumentSubType({
          id: selectedEntity?.id,
          body: payload,
        })
      : createDocumentSubType(payload);

    try {
      const resultAction = await dispatch(action).unwrap();
      if (resultAction?.data) {
        showNotification(
          selectedEntity
            ? "Document Sub Type Updated Successfully"
            : "Document Sub Type Created Successfully",
          "success"
        );
        onSubmit();
      }
    } catch (error) {
      // Errors are handled by the slice's rejectWithValue, no need to show notification here
    } finally {
      setSaving(false);
    }
  };

  const colSpan = screenType.isPortrait ? 24 : 12;

  const formProps = {
    form: form,
    name: "create-edit-document-sub-type",
    heading: selectedEntity ? "Edit Document Sub Type" : "Add New Document Sub Type",
    initialValues: {
      status: "ACTIVE",
    },
    formElements: [
      {
        name: "documentSubTypeName",
        colSpan: 24,
        label: "Document Sub Type Name",
        placeholder: "e.g. Passport - Front",
        type: "Input",
        allowClear: true,
        rules: [{ required: true, message: "Please enter a document sub type name" }],
      },
      {
        name: "status",
        label: "Status",
        placeholder: "Status",
        type: "Select",
        colSpan: colSpan,
        showSearch: true,
        allowClear: true,
        options: [
          { value: "ACTIVE", label: "ACTIVE" },
          { value: "INACTIVE", label: "INACTIVE" },
        ],
        rules: [{ required: true, message: "Please select a status" }],
      },
    ],
    extraButtons: [
      {
        htmlType: "button",
        colSpan: 5,
        loading: false,
        submitButtonText: "Cancel",
        onClick: onClose,
      },
      {
        htmlType: "button",
        colSpan: 5,
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
      },
      {
        htmlType: "submit",
        colSpan: 5,
        loading: saving,
        submitButtonText: "Save",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFinish,
    onFinishFailed: () => {
      return;
    },
    onValuesChange: () => {
      return;
    },
  };

  return (
    <Drawer
      maskClosable={false}
      title={<strong>{formProps.heading}</strong>}
      className="form-drawer"
      width={
        screenType.isPortrait
          ? "90%"
          : screenType.isLg || screenType.isXl
          ? 720
          : 500
      }
      onClose={onClose}
      open={visible}
    >
      <GenericFormComponent {...formProps} />
    </Drawer>
  );
};

export default CreateEditDocumentSubTypeComponent;